// src/v3/ChooseButton/ChooseButton.tsx
import { cx as cx2 } from "class-variance-authority";
import {
  useContext,
  useId,
  useRef
} from "react";
import { useRadio } from "react-aria";

// src/v3/ChooseButton/ChooseButtonGroup.tsx
import { cx } from "class-variance-authority";
import {
  createContext
} from "react";
import { useRadioGroup } from "react-aria";
import { useRadioGroupState } from "react-stately";

// src/v3/Typography/index.tsx
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var typography = cva("", {
  variants: {
    variant: {
      h1: "ui-text-[2.5rem] ui-tracking-tight md:ui-text-[3.75rem] lg:ui-text-[5rem]",
      h2: "ui-text-[2.25rem] ui-tracking-normal md:ui-text-[3rem] lg:ui-text-[4.25rem] lg:ui-tracking-[-2px]",
      h3: "ui-text-[2rem] ui-tracking-normal md:ui-text-[2.5rem] lg:ui-text-[3.5rem]",
      h4: "ui-text-[1.75rem] ui-tracking-normal md:ui-text-[2rem] lg:ui-text-[2.5rem]",
      h5: "ui-text-[1.25rem] ui-tracking-normal lg:ui-leading-10",
      h6: "ui-text-[1.25rem] ui-tracking-normal",
      "body-large": "ui-text-[1.125rem] ui-tracking-normal",
      "title-large": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-8xl xl:ui-text-[7.5rem]",
      "title-small": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-[5rem]",
      base: "ui-text-base ui-tracking-normal",
      "body-small": "ui-text-[0.875rem] ui-tracking-normal",
      "eyebrow-large": "ui-text-base",
      "eyebrow-small": "ui-text-[0.75rem]",
      unset: ""
    },
    align: {
      left: "ui-text-left",
      center: "ui-text-center",
      right: "ui-text-right",
      justify: "ui-text-justify",
      centerMdLeft: "ui-text-center md:ui-text-left",
      centerLgLeft: "ui-text-center lg:ui-text-left"
    },
    color: {
      light: "ui-text-sandstone-200",
      dark: "ui-text-sandstone-500",
      disabled: "ui-text-gray-300",
      error: "ui-text-red-600",
      gray: "ui-text-gray-400",
      success: "ui-text-green-600",
      inherit: "ui-text-inherit",
      unset: ""
    },
    font: {
      sans: "ui-font-sans",
      serif: "ui-font-serif",
      "serif-italic": "ui-font-serif ui-italic"
    },
    height: {
      none: "ui-leading-none",
      tight: "ui-leading-tight",
      snug: "ui-leading-snug",
      normal: "ui-leading-normal",
      relaxed: "ui-leading-relaxed",
      loose: "ui-leading-loose",
      heading: "ui-leading-heading"
    },
    weight: {
      thin: "ui-font-thin",
      extralight: "ui-font-extralight",
      light: "ui-font-light",
      normal: "ui-font-normal",
      medium: "ui-font-medium",
      semibold: "ui-font-semibold",
      bold: "ui-font-bold",
      extrabold: "ui-font-extrabold",
      black: "ui-font-black"
    }
  },
  defaultVariants: {
    variant: "base",
    height: "normal",
    weight: "normal",
    font: "sans"
  }
});
function Typography({
  align = "left",
  Element = "p",
  children,
  className,
  color = "dark",
  font = "sans",
  height,
  variant = "base",
  weight = "normal",
  style,
  ...props
}) {
  let _weight = weight;
  if (variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4") {
    _weight = "light";
  }
  return /* @__PURE__ */ jsx(Element, {
    className: typography({
      align,
      color,
      height,
      variant,
      weight: _weight,
      font,
      className
    }),
    style,
    ...props,
    children
  });
}
Typography.displayName = "Typography";

// src/v3/ChooseButton/ChooseButtonGroup.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var ChooseButtonGroupContext = createContext(
  null
);
function ChooseButtonGroup(props) {
  const {
    children,
    hideLabel = false,
    label,
    description,
    aboveButtonGroupSlot,
    onChange: _onChange,
    ...rest
  } = props;
  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps, descriptionProps } = useRadioGroup(
    props,
    state
  );
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      label ? /* @__PURE__ */ jsx2(Typography, {
        className: cx("ui-mb-2 lg:ui-mb-1 lg:ui-mt-2 lg:ui-text-xl", {
          "ui-mb-4 lg:ui-mb-4": !description
        }),
        "data-cy": "choose-btn-group-title",
        children: /* @__PURE__ */ jsx2("span", {
          ...labelProps,
          className: cx(labelProps.className, {
            "ui-sr-only": Boolean(hideLabel)
          }),
          children: label
        })
      }) : null,
      description ? /* @__PURE__ */ jsx2(Typography, {
        className: "ui-mb-4",
        "data-cy": "choose-btn-group-description",
        children: /* @__PURE__ */ jsx2("span", {
          ...descriptionProps,
          children: description
        })
      }) : null,
      Boolean(aboveButtonGroupSlot) && aboveButtonGroupSlot,
      /* @__PURE__ */ jsx2("div", {
        ...rest,
        ...radioGroupProps,
        children: /* @__PURE__ */ jsx2("div", {
          className: "ui-relative ui-flex ui-w-full ui-flex-col ui-gap-2.5",
          children: /* @__PURE__ */ jsx2(ChooseButtonGroupContext.Provider, {
            value: state,
            children
          })
        })
      })
    ]
  });
}
ChooseButtonGroup.displayName = "ChooseButtonGroup";
var ChooseButtonGroup_default = ChooseButtonGroup;

// src/v3/ChooseButton/ChooseButton.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function ChooseButton(props) {
  const { children, className } = props;
  const state = useContext(ChooseButtonGroupContext);
  const ref = useRef(null);
  const id = useId();
  const { inputProps, isSelected, isDisabled } = useRadio(
    props,
    state,
    ref
  );
  return /* @__PURE__ */ jsxs2("div", {
    children: [
      /* @__PURE__ */ jsx3("input", {
        checked: isSelected,
        "data-cy": "choose-button-input",
        ...inputProps,
        className: "ui-peer ui-absolute ui-appearance-none",
        id,
        ref
      }),
      /* @__PURE__ */ jsx3("label", {
        className: cx2(
          className,
          "ui-cursor-pointer ui-overflow-hidden ui-rounded-lg ui-border hover:ui-border-white hover:ui-bg-white",
          {
            "ui-pointer-events-none ui-cursor-default ui-opacity-30": isDisabled,
            "ui-border-sandstone-500 ui-bg-white hover:ui-border-sandstone-500": isSelected,
            "ui-border-gray-150 ui-bg-gray-150": !isSelected
          }
        ),
        "data-cy": "choose-button-label",
        "data-value": props.value,
        htmlFor: id,
        children
      })
    ]
  });
}
ChooseButton.displayName = "ChooseButton";
var ChooseButton_default = ChooseButton;

// src/v3/ChooseButton/ChooseButtonCheck.tsx
import { cx as cx4 } from "class-variance-authority";

// src/icons/CircularFillCheckmarkIcon.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function CircularFillCheckmarkIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs3("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx4("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx4("circle", {
        cx: 12,
        cy: 12,
        fill: "currentColor",
        r: 11,
        stroke: "currentColor"
      }),
      /* @__PURE__ */ jsx4("path", {
        d: "M7.75 12.5L10.9167 15.6666L17.25 9.33331",
        stroke: "white",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: 2
      })
    ]
  });
}

// src/react-intl/FormattedMessage.tsx
import { FormattedMessage as ReactIntlFormattedMessage } from "react-intl";

// src/v3/BodyLink/index.tsx
import { forwardRef, useContext as useContext2 } from "react";
import { cx as cx3 } from "class-variance-authority";

// src/contexts/ComponentProvider.tsx
import { createContext as createContext2, useMemo } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Context = createContext2(null);
function ComponentProvider({
  children,
  ImageComponent,
  LinkComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx5(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/v3/BodyLink/index.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var BodyLink = forwardRef(
  function BodyLink2({
    children,
    className,
    color = "sandstone-500",
    external = false,
    href = "",
    onClick,
    ...rest
  }, ref) {
    const context = useContext2(Context);
    const LinkComponent = external || !(context == null ? void 0 : context.LinkComponent) ? "a" : context.LinkComponent;
    return /* @__PURE__ */ jsx6(LinkComponent, {
      "data-cy": "body_link",
      ...rest,
      className: cx3(`ui-underline ui-text-${color}`, className),
      href,
      onClick: (evt) => onClick == null ? void 0 : onClick(evt),
      ref,
      children
    });
  }
);
BodyLink.displayName = "BodyLink";

// src/react-intl/htmlFormatting.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var htmlFormatting = {
  br: /* @__PURE__ */ jsx7("br", {}),
  w: /* @__PURE__ */ jsx7("wbr", {}),
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
  break: function breakTag() {
    return /* @__PURE__ */ jsx7("br", {});
  },
  strong: function strong(chunks) {
    return /* @__PURE__ */ jsx7("strong", {
      children: chunks
    });
  },
  b: function b(chunks) {
    return /* @__PURE__ */ jsx7("strong", {
      children: chunks
    });
  },
  em: function em(chunks) {
    return /* @__PURE__ */ jsx7("em", {
      children: chunks
    });
  },
  i: function i(chunks) {
    return /* @__PURE__ */ jsx7("em", {
      children: chunks
    });
  },
  u: function u(chunks) {
    return /* @__PURE__ */ jsx7("span", {
      className: "ui-underline",
      children: chunks
    });
  },
  sup: function sup(chunks) {
    return /* @__PURE__ */ jsx7("sup", {
      className: "ui-font-serif ui-italic ui-text-blue-200 ui-font-semibold ui-tracking-wider",
      children: chunks
    });
  },
  li: function li(chunks) {
    return /* @__PURE__ */ jsx7("li", {
      children: chunks
    });
  },
  ExternalLink: function ExternalLink(href) {
    return /* @__PURE__ */ jsx7(BodyLink, {
      href,
      rel: "noreferrer",
      target: "_blank",
      children: href
    });
  },
  EmailLink: function EmailLink(email) {
    return /* @__PURE__ */ jsx7(BodyLink, {
      href: `mailto:${email}`,
      children: email
    });
  }
};

// src/react-intl/stringFormatting.ts
var stringFormatting = Object.entries(
  htmlFormatting
).reduce((obj, [key, value]) => {
  const type = typeof value;
  if (type === "string") {
    return { ...obj, [key]: value };
  } else if (type === "function") {
    return { ...obj, [key]: (chunks) => chunks };
  }
  return { ...obj, [key]: "" };
}, {});

// src/react-intl/getLocaleFormatting.tsx
var getLocaleFormatting = (options = {}) => {
  const { stripHtml } = options;
  if (stripHtml) {
    return stringFormatting;
  }
  return htmlFormatting;
};
getLocaleFormatting.displayName = "getLocaleFormatting";
var getLocaleFormatting_default = getLocaleFormatting;

// src/react-intl/FormattedMessage.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function FormattedMessage({
  id,
  values: optionalValues,
  ...props
}) {
  const localeFormatting = getLocaleFormatting_default();
  const values = {
    ...localeFormatting,
    ...optionalValues
  };
  return /* @__PURE__ */ jsx8(ReactIntlFormattedMessage, {
    id,
    values,
    ...props
  });
}
function t(id, values) {
  return /* @__PURE__ */ jsx8(FormattedMessage, {
    id,
    values
  });
}
FormattedMessage.displayName = "FormattedMessage";

// src/v3/ChooseButton/ChooseButtonCheck.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function ChooseButtonCheck({
  className = "ui-size-6 ui-text-slate-200",
  chosen
}) {
  return /* @__PURE__ */ jsx9(CircularFillCheckmarkIcon, {
    "aria-hidden": !chosen,
    "aria-label": t("pdp_selected_check_label"),
    className: cx4(
      "ui-inline-flex ui-self-center",
      {
        "ui-invisible": !chosen,
        "ui-visible": chosen
      },
      className
    ),
    "data-cy": "choose-btn-check"
  });
}
ChooseButtonCheck.displayName = "ChooseButtonCheck";
var ChooseButtonCheck_default = ChooseButtonCheck;

// src/v3/ChooseButton/SecondaryChooseButton.tsx
import { cx as cx5 } from "class-variance-authority";
import {
  useContext as useContext3,
  useId as useId2,
  useRef as useRef2
} from "react";
import { useRadio as useRadio2 } from "react-aria";
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
function SecondaryChooseButton(props) {
  const { children, className = "" } = props;
  const state = useContext3(ChooseButtonGroupContext);
  const ref = useRef2(null);
  const id = useId2();
  const { inputProps, isSelected, isDisabled } = useRadio2(
    props,
    state,
    ref
  );
  return /* @__PURE__ */ jsxs4(Fragment2, {
    children: [
      /* @__PURE__ */ jsx10("label", {
        className: cx5(
          className,
          "ui-flex ui-cursor-pointer ui-items-center ui-justify-center ui-rounded-full ui-border ui-border-current ui-px-4 ui-py-2 ui-text-sandstone-500 maxlg:ui-w-full",
          {
            "ui-pointer-events-none ui-cursor-default ui-opacity-30": isDisabled,
            "ui-bg-opacity-70": !isSelected
          }
        ),
        "data-cy": "secondary-choose-button-label",
        htmlFor: id,
        children
      }),
      /* @__PURE__ */ jsx10("input", {
        ...inputProps,
        className: "ui-peer ui-absolute ui-appearance-none",
        id,
        ref
      })
    ]
  });
}
SecondaryChooseButton.displayName = "SecondaryChooseButton";
var SecondaryChooseButton_default = SecondaryChooseButton;
export {
  ChooseButton_default as ChooseButton,
  ChooseButtonCheck_default as ChooseButtonCheck,
  ChooseButtonGroup_default as ChooseButtonGroup,
  SecondaryChooseButton_default as SecondaryChooseButton
};
//# sourceMappingURL=index.mjs.map